import {
  ExpandLess,
  ExpandMore,
  Settings,
} from "@mui/icons-material";
import {
  BookOpen,
  Brain,
  BrainCircuit,
  Briefcase,
  Building,
  Compass,
  Calendar,
  CalendarX,
  CheckCircle,
  CheckSquare,
  ClipboardList,
  Code,
  Columns,
  Copy,
  Database,
  Download,
  Factory,
  FileCode,
  FileEdit,
  FileOutput,
  FilePlus,
  FileSearch,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  FolderPlus,
  FolderTree,
  Globe,
  GripVertical,
  Hammer,
  HelpCircle,
  Image,
  ImagePlus,
  Inbox,
  Layers,
  Library,
  List,
  ListPlus,
  Mail,
  MapPin,
  MessageSquare,
  Microscope,
  MousePointer,
  Newspaper,
  Paperclip,
  Pencil,
  Phone,
  Play,
  Plus,
  RefreshCw,
  Rocket,
  RowsIcon,
  Search,
  SearchCode,
  Send,
  Share2,
  SlidersHorizontal,
  Sparkles,
  Table,
  Terminal,
  Trash2,
  TrendingUp,
  Upload,
  UserSearch,
  Users,
  Wrench,
  Zap,
} from "lucide-react";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Link,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useWhitelabel } from "../context/WhitelabelContext";
import { AttachmentBlock, Employee, FileBlock, InstallBlock, MemoryBlock, MessageBlock, ToolBlock } from "../types";
import { addImageDimensions } from "../utils";
import { sanitizeFilename } from "../utils";
import LoadingCursor from "./LoadingCursor";

// Extract domain from URL for favicon
const getDomainFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    return null;
  }
};

// Get favicon URL for read_website_provide_summary tool
const getFaviconUrl = (toolName: string, args?: any, toolLogo?: string): string | null => {
  // Use tool logo if provided (e.g., from Composio tools)
  if (toolLogo) {
    return toolLogo;
  }

  const name = toolName.toLowerCase();
  if (name === "read_website_provide_summary" && args?.url) {
    const domain = getDomainFromUrl(args.url);
    if (domain) {
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    }
  }
  return null;
};

// Map tool names to display names
const TOOL_DISPLAY_NAMES: Record<string, string> = {
  // Search & Research Tools
  search_engine_results: "Web Search",
  search_news: "News Search",
  research_subject_provide_summary: "Research Agent",
  research_task: "Deep Research",

  // Website Reading Tools
  read_website_provide_summary: "Read Website",
  read_website_provide_answer: "Quick Website Answer",

  // Knowledge Base Tools
  query_knowledge_base: "Search Knowledge Base",
  list_knowledge_base_folders: "Browse Folders",
  find_knowledge_base_documents: "Find Documents",
  get_knowledge_base_document: "Get Document",

  // Memory Tools
  save_memory: "Save Memory",
  search_memories: "Search Memories",
  recall_chat_conversations: "Recall Conversations",

  // Audience Tools
  audience_enrich: "Enrich Contact",
  audience_preview: "Preview Audience",
  search_audience_segments: "Find Segments",
  list_audience_industries: "List Industries",

  // List Management Tools
  get_lists: "Get Lists",
  get_list_rows: "Get List Data",
  create_list: "Create List",
  create_list_with_columns: "Create List with Columns",
  add_list_row: "Add Row",
  add_list_row_with_data: "Add Row with Data",
  add_list_column: "Add Column",
  update_list_row: "Update Row",
  update_data_record: "Update Record",
  insert_data_record: "Insert Record",

  // Document Tools
  create_document: "Create Document",
  append_to_document: "Add to Document",
  email_document: "Email Document",
  list_documents: "List Documents",
  get_document_content: "Read Document",
  get_document_status: "Document Status",

  // Browser Automation
  browser_task: "Browser Task",

  // Sequence Tools
  list_sequences: "List Sequences",
  get_sequence: "Get Sequence",
  create_sequence: "Create Sequence",
  get_sequence_metrics: "Sequence Metrics",

  // Delegate Task Tools (uses generic term - whitelabel handled in getToolDisplayName)
  list_employees: "List Team Members",
  delegate_task: "Delegate Task",

  // Scheduled Task Tools
  schedule_task: "Schedule Task",
  list_scheduled_tasks: "List Scheduled Tasks",
  cancel_scheduled_task: "Cancel Scheduled Task",

  // Project File Tools
  project_ls: "List Files",
  ls: "List Files",
  project_tree: "File Tree",
  tree: "File Tree",
  project_cat: "Read File",
  cat: "Read File",
  project_touch: "Create File",
  touch: "Create File",
  project_write: "Write File",
  write: "Write File",
  project_append: "Append to File",
  append: "Append to File",
  project_rm: "Delete File",
  rm: "Delete File",
  project_mv: "Move File",
  mv: "Move File",
  project_cp: "Copy File",
  cp: "Copy File",
  project_mkdir: "Create Folder",
  mkdir: "Create Folder",
  project_run: "Run Code",
  run: "Run Code",
  project_publish: "Publish Project",
  publish: "Publish Project",
  project_glob: "Find Files",
  glob: "Find Files",
  project_grep: "Search in Files",
  grep: "Search in Files",
  project_read: "Read File Lines",
  read: "Read File Lines",
  project_edit: "Edit File",
  edit: "Edit File",
  project_validate_html: "Validate HTML",
  validate_html: "Validate HTML",

  // Tool Router Tools
  search_tools: "Search Tools",
  execute_tools: "Execute Tools",
  request_install_tool: "Connect App",
  get_tool_schema: "Get Tool Schema",

  // Remote Execution Tools
  remote_workbench: "Run Python Code",
  remote_bash: "Run Shell Command",

  // Stock Image Tools
  get_stock_image: "Get Stock Image",
  stock_search: "Search Images",
  stock_download: "Download Image",

  // Agent Tools
  list_agents: "List Agents",

  // Voice Tools
  voice_call_transfer_tool: "Transfer Call",
  voice_sms_tool: "Send SMS",

  // Google Maps Tools
  google_maps_search: "Google Maps Search",
  google_maps_place: "Google Maps Place",

  // Google Trends Tools
  google_trends_autocomplete: "Google Trends",

  // Google Jobs Tools
  google_jobs_search: "Google Jobs Search",

  // Platform MCP Tools
  platform_list_capabilities: "Browse Capabilities",
  platform_get_action_details: "Get Action Details",
  platform_execute_action: "Execute Action",
  platform_list_companies: "List Companies",
};

// Get formatted display name for a tool
// employeeTerm is optional - used for whitelabel support on employee-related tools
export const getToolDisplayName = (toolName: string, employeeTerm?: string): string => {
  const baseName = TOOL_DISPLAY_NAMES[toolName] || toolName;

  // Handle employee-related tools with whitelabel terminology
  if (employeeTerm) {
    if (toolName === "list_employees") {
      return `List ${employeeTerm}s`;
    } else if (toolName === "delegate_task") {
      return `Delegate to ${employeeTerm}`;
    }
  }

  return baseName;
};

// Map tool names to Lucide icons
const getToolIcon = (toolName: string) => {
  const name = toolName.toLowerCase();
  const iconSize = 16;

  // Search & Research Tools
  if (name === "search_engine_results") {
    return <Search size={iconSize} />;
  } else if (name === "search_news") {
    return <Newspaper size={iconSize} />;
  } else if (name === "research_subject_provide_summary") {
    return <Microscope size={iconSize} />;
  } else if (name === "research_task") {
    return <BrainCircuit size={iconSize} />;
  }

  // Website Reading Tools
  else if (name === "read_website_provide_summary") {
    return <Globe size={iconSize} />;
  } else if (name === "read_website_provide_answer") {
    return <HelpCircle size={iconSize} />;
  }

  // Knowledge Base Tools
  else if (name === "query_knowledge_base") {
    return <BookOpen size={iconSize} />;
  } else if (name === "list_knowledge_base_folders") {
    return <FolderOpen size={iconSize} />;
  } else if (name === "find_knowledge_base_documents") {
    return <FileSearch size={iconSize} />;
  } else if (name === "get_knowledge_base_document") {
    return <FileText size={iconSize} />;
  }

  // Memory Tools
  else if (name === "save_memory") {
    return <Sparkles size={iconSize} />;
  } else if (name === "search_memories") {
    return <Sparkles size={iconSize} />;
  } else if (name === "recall_chat_conversations") {
    return <MessageSquare size={iconSize} />;
  }

  // Audience Tools
  else if (name === "audience_enrich") {
    return <UserSearch size={iconSize} />;
  } else if (name === "audience_preview") {
    return <Users size={iconSize} />;
  } else if (name === "search_audience_segments") {
    return <Layers size={iconSize} />;
  } else if (name === "list_audience_industries") {
    return <Factory size={iconSize} />;
  }

  // List Management Tools
  else if (name === "get_lists") {
    return <List size={iconSize} />;
  } else if (name === "get_list_rows") {
    return <RowsIcon size={iconSize} />;
  } else if (name === "create_list") {
    return <ListPlus size={iconSize} />;
  } else if (name === "create_list_with_columns") {
    return <Table size={iconSize} />;
  } else if (name === "add_list_row") {
    return <Plus size={iconSize} />;
  } else if (name === "add_list_row_with_data") {
    return <ListPlus size={iconSize} />;
  } else if (name === "add_list_column") {
    return <Columns size={iconSize} />;
  } else if (name === "update_list_row") {
    return <Pencil size={iconSize} />;
  } else if (name === "update_data_record") {
    return <Pencil size={iconSize} />;
  } else if (name === "insert_data_record") {
    return <Plus size={iconSize} />;
  }

  // Document Tools
  else if (name === "create_document") {
    return <FilePlus size={iconSize} />;
  } else if (name === "append_to_document") {
    return <FileOutput size={iconSize} />;
  } else if (name === "email_document") {
    return <Mail size={iconSize} />;
  } else if (name === "list_documents") {
    return <Library size={iconSize} />;
  } else if (name === "get_document_content") {
    return <FileText size={iconSize} />;
  } else if (name === "get_document_status") {
    return <ClipboardList size={iconSize} />;
  }

  // Browser Automation
  else if (name === "browser_task") {
    return <MousePointer size={iconSize} />;
  }

  // Sequence Tools
  else if (name === "list_sequences") {
    return <List size={iconSize} />;
  } else if (name === "get_sequence") {
    return <GripVertical size={iconSize} />;
  } else if (name === "create_sequence") {
    return <ListPlus size={iconSize} />;
  } else if (name === "get_sequence_metrics") {
    return <Table size={iconSize} />;
  }

  // Delegate Task Tools
  else if (name === "list_employees") {
    return <Users size={iconSize} />;
  } else if (name === "delegate_task") {
    return <Share2 size={iconSize} />;
  }

  // Scheduled Task Tools
  else if (name === "schedule_task") {
    return <Calendar size={iconSize} />;
  } else if (name === "list_scheduled_tasks") {
    return <ClipboardList size={iconSize} />;
  } else if (name === "cancel_scheduled_task") {
    return <CalendarX size={iconSize} />;
  }

  // Project File Tools
  else if (name === "project_ls" || name === "ls") {
    return <FolderOpen size={iconSize} />;
  } else if (name === "project_tree" || name === "tree") {
    return <FolderTree size={iconSize} />;
  } else if (name === "project_cat" || name === "cat" || name === "project_read" || name === "read") {
    return <FileText size={iconSize} />;
  } else if (name === "project_touch" || name === "touch") {
    return <FilePlus size={iconSize} />;
  } else if (name === "project_write" || name === "write" || name === "project_edit" || name === "edit") {
    return <FileEdit size={iconSize} />;
  } else if (name === "project_append" || name === "append") {
    return <FileOutput size={iconSize} />;
  } else if (name === "project_rm" || name === "rm") {
    return <Trash2 size={iconSize} />;
  } else if (name === "project_mv" || name === "mv") {
    return <RefreshCw size={iconSize} />;
  } else if (name === "project_cp" || name === "cp") {
    return <Copy size={iconSize} />;
  } else if (name === "project_mkdir" || name === "mkdir") {
    return <FolderPlus size={iconSize} />;
  } else if (name === "project_run" || name === "run") {
    return <Play size={iconSize} />;
  } else if (name === "project_publish" || name === "publish") {
    return <Rocket size={iconSize} />;
  } else if (name === "project_glob" || name === "glob") {
    return <FileSearch size={iconSize} />;
  } else if (name === "project_grep" || name === "grep") {
    return <SearchCode size={iconSize} />;
  } else if (name === "project_validate_html" || name === "validate_html") {
    return <CheckSquare size={iconSize} />;
  }

  // Tool Router Tools
  else if (name === "search_tools") {
    return <Wrench size={iconSize} />;
  } else if (name === "execute_tools") {
    return <Zap size={iconSize} />;
  } else if (name === "request_install_tool") {
    return <Upload size={iconSize} />;
  } else if (name === "get_tool_schema") {
    return <SlidersHorizontal size={iconSize} />;
  }

  // Remote Execution Tools
  else if (name === "remote_workbench") {
    return <Code size={iconSize} />;
  } else if (name === "remote_bash") {
    return <Terminal size={iconSize} />;
  }

  // Stock Image Tools
  else if (name === "get_stock_image") {
    return <ImagePlus size={iconSize} />;
  } else if (name === "stock_search") {
    return <Image size={iconSize} />;
  } else if (name === "stock_download") {
    return <Image size={iconSize} />;
  }

  // Agent Tools
  else if (name === "list_agents") {
    return <Briefcase size={iconSize} />;
  }

  // Voice Tools
  else if (name === "voice_call_transfer_tool") {
    return <Phone size={iconSize} />;
  } else if (name === "voice_sms_tool") {
    return <Send size={iconSize} />;
  }

  // Google Maps Tools
  else if (name === "google_maps_search") {
    return <MapPin size={iconSize} />;
  } else if (name === "google_maps_place") {
    return <MapPin size={iconSize} />;
  }

  // Google Trends Tools
  else if (name === "google_trends_autocomplete") {
    return <TrendingUp size={iconSize} />;
  }

  // Google Jobs Tools
  else if (name === "google_jobs_search") {
    return <Building size={iconSize} />;
  }

  // Platform MCP Tools
  else if (name === "platform_list_capabilities") {
    return <Compass size={iconSize} />;
  } else if (name === "platform_get_action_details") {
    return <FileSearch size={iconSize} />;
  } else if (name === "platform_execute_action") {
    return <Zap size={iconSize} />;
  } else if (name === "platform_list_companies") {
    return <Building size={iconSize} />;
  }

  // Fallback patterns for any tools not explicitly listed
  else if (name.includes("list") && !name.includes("knowledge")) {
    return <Table size={iconSize} />;
  } else if (name.includes("news")) {
    return <Newspaper size={iconSize} />;
  } else if (name.includes("research")) {
    return <Microscope size={iconSize} />;
  } else if (name.includes("memor")) {
    return <Sparkles size={iconSize} />;
  } else if (name.includes("search")) {
    return <Search size={iconSize} />;
  } else if (name.includes("browser") || name.includes("web")) {
    return <Globe size={iconSize} />;
  } else if (name.includes("code") || name.includes("execute")) {
    return <Code size={iconSize} />;
  } else if (name.includes("database") || name.includes("storage")) {
    return <Database size={iconSize} />;
  } else if (name.includes("document") || name.includes("doc")) {
    return <FileText size={iconSize} />;
  } else if (name.includes("audience") || name.includes("people")) {
    return <Users size={iconSize} />;
  } else if (name.includes("folder")) {
    return <FolderOpen size={iconSize} />;
  } else if (name.includes("email") || name.includes("mail")) {
    return <Mail size={iconSize} />;
  } else if (name.includes("file")) {
    return <FileText size={iconSize} />;
  } else if (name.includes("image") || name.includes("photo")) {
    return <Image size={iconSize} />;
  } else if (name.includes("send") || name.includes("message")) {
    return <Send size={iconSize} />;
  }

  return <Hammer size={iconSize} />;
};

// Component for rendering reasoning block with its own state
const ReasoningBlock = ({ content }: { content: string }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box sx={{ my: 2 }}>
      <Card
        variant="outlined"
        sx={{
          bgcolor: "transparent",
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}
          onClick={() => setExpanded(!expanded)}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: "text.secondary",
            }}
          >
            <Brain size={18} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight="medium">
              Reasoning
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {content.trim().split(/\s+/).filter(Boolean).length} words
            </Typography>
          </Box>
          <IconButton size="small" aria-label="expand">
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                fontSize: "0.875rem",
                color: "text.secondary",
              }}
            >
              {content}
            </Typography>
          </Box>
        </Collapse>
      </Card>
    </Box>
  );
};

// Component for rendering tool arguments in a readable format
const ToolArguments = ({ args }: { args: any }) => {
  if (!args || typeof args !== "object") {
    return (
      <Typography variant="body2" color="text.secondary">
        {String(args)}
      </Typography>
    );
  }

  // Unwrap LangChain format: {"args": [...], "config": {}} -> first element of args array
  let displayArgs = args;
  if (args.args && Array.isArray(args.args) && args.args.length > 0) {
    displayArgs = args.args[0];
  }

  const renderValue = (value: any, key?: string): React.ReactNode => {
    // Handle null/undefined
    if (value === null || value === undefined) {
      return (
        <Chip
          label={String(value)}
          size="small"
          sx={{ height: 20, fontSize: "0.7rem" }}
        />
      );
    }

    // Handle booleans
    if (typeof value === "boolean") {
      return (
        <Chip
          label={String(value)}
          size="small"
          color={value ? "success" : "default"}
          sx={{ height: 20, fontSize: "0.7rem" }}
        />
      );
    }

    // Handle URLs
    if (
      typeof value === "string" &&
      (value.startsWith("http://") || value.startsWith("https://"))
    ) {
      return (
        <Link
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: "0.875rem", wordBreak: "break-all" }}
        >
          {value}
        </Link>
      );
    }

    // Handle strings
    if (typeof value === "string") {
      // Long strings get a scrollable box
      if (value.length > 100) {
        return (
          <Box
            component="pre"
            sx={{
              mt: 0.5,
              p: 1,
              bgcolor: "background.default",
              borderRadius: 1,
              fontSize: "0.75rem",
              overflow: "auto",
              maxHeight: "150px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {value}
          </Box>
        );
      }
      return (
        <Typography variant="body2" component="span">
          {value}
        </Typography>
      );
    }

    // Handle numbers
    if (typeof value === "number") {
      return (
        <Chip
          label={String(value)}
          size="small"
          sx={{ height: 20, fontSize: "0.7rem" }}
        />
      );
    }

    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary">
            []
          </Typography>
        );
      }
      return (
        <Box sx={{ pl: 2, borderLeft: 2, borderColor: "divider" }}>
          {value.map((item, idx) => (
            <Box key={idx} sx={{ my: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                component="span"
                sx={{ mr: 1 }}
              >
                [{idx}]
              </Typography>
              {renderValue(item)}
            </Box>
          ))}
        </Box>
      );
    }

    // Handle objects
    if (typeof value === "object") {
      const entries = Object.entries(value);
      if (entries.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary">
            {"{}"}
          </Typography>
        );
      }
      return (
        <Box sx={{ pl: 2, borderLeft: 2, borderColor: "divider", mt: 0.5 }}>
          {entries.map(([k, v]) => (
            <Box key={k} sx={{ my: 0.5 }}>
              <Typography
                variant="body2"
                fontWeight="medium"
                component="span"
                sx={{ mr: 1 }}
              >
                {k}:
              </Typography>
              {renderValue(v, k)}
            </Box>
          ))}
        </Box>
      );
    }

    // Fallback to JSON stringify
    return (
      <Typography variant="body2" color="text.secondary">
        {JSON.stringify(value)}
      </Typography>
    );
  };

  return (
    <Box>
      {Object.entries(displayArgs).map(([key, value]) => (
        <Box key={key} sx={{ mb: 1 }}>
          <Typography
            variant="body2"
            fontWeight="medium"
            color="primary"
            sx={{ mb: 0.5 }}
          >
            {key}
          </Typography>
          <Box sx={{ ml: 2 }}>{renderValue(value, key)}</Box>
        </Box>
      ))}
    </Box>
  );
};

// Component for rendering the Composio install button
const ComposioInstallButton = ({ installData }: { installData: { slug: string; composioSlug: string; logo: string; name: string; message: string } }) => {
  // Format display name - capitalize first letter of each word
  const displayName = installData.name || installData.slug || "Integration";

  return (
    <Card variant="outlined" sx={{ my: 1.5, p: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
          src={installData.logo}
          variant="rounded"
          sx={{ width: 32, height: 32, bgcolor: "transparent" }}
        >
          {displayName[0]?.toUpperCase()}
        </Avatar>
        <Typography variant="body1">
          Connect to {displayName}
        </Typography>
      </Box>
      <Button
        variant="contained"
        size="small"
        sx={{
          bgcolor: "grey.900",
          color: "white",
          "&:hover": {
            bgcolor: "grey.800",
          },
        }}
        onClick={() => {
          window.dispatchEvent(new CustomEvent("openComposioInstaller", {
            detail: {
              slug: installData.slug,
              composioSlug: installData.composioSlug,
              logo: installData.logo,
              name: displayName
            }
          }));
        }}
      >
        Connect
      </Button>
    </Card>
  );
};

// Component for rendering a save_memory tool as a memory card
const MemoryToolCard = ({ tool }: { tool: ToolBlock }) => {
  // Parse the output to get memory details
  let memoryData: any = null;
  try {
    if (tool.output) {
      memoryData = JSON.parse(tool.output);
    }
  } catch {
    // Not JSON, use args as fallback
  }

  const content = memoryData?.memory?.content || tool.args?.content || "Memory saved";
  const category = memoryData?.memory?.category || tool.args?.category;
  const status = memoryData?.status || (tool.status === "completed" ? "saved" : "saving");

  const getCategoryLabel = (cat?: string) => {
    switch (cat) {
      case "preference": return "Preference";
      case "fact": return "Fact";
      case "instruction": return "Instruction";
      case "context": return "Context";
      default: return null;
    }
  };

  return (
    <Box sx={{ my: 2 }}>
      <Card
        variant="outlined"
        sx={{
          bgcolor: "transparent",
          borderColor: status === "saved" ? "primary.main" : "divider",
          borderWidth: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: status === "saved" ? "primary.main" : "action.hover",
            }}
          >
            {status === "saved" ? <CheckCircle size={20} /> : <Sparkles size={20} />}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography variant="body2" fontWeight="medium">
                {status === "saved" ? "Memory Saved" : "Saving Memory..."}
              </Typography>
              {category && getCategoryLabel(category) && (
                <Chip
                  label={getCategoryLabel(category)}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              )}
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {content}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Settings />}
            onClick={() => {
              window.dispatchEvent(new CustomEvent("openMemoriesDialog"));
            }}
          >
            Manage
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

// Helper function to get employee info for delegate_task tool
const getEmployeeForDelegateTask = (tool: ToolBlock, employees?: Employee[]): Employee | null => {
  if (tool.name !== "delegate_task" || !employees || employees.length === 0) {
    return null;
  }

  // Extract employeeId from tool args (handle both snake_case and camelCase)
  let employeeId: string | null = null;
  if (tool.args) {
    // Handle direct args - check both employee_id (snake_case from backend) and employeeId (camelCase)
    if (tool.args.employee_id) {
      employeeId = tool.args.employee_id;
    } else if (tool.args.employeeId) {
      employeeId = tool.args.employeeId;
    }
    // Handle LangChain format: {"args": [...], "config": {}}
    else if (tool.args.args && Array.isArray(tool.args.args) && tool.args.args.length > 0) {
      employeeId = tool.args.args[0]?.employee_id || tool.args.args[0]?.employeeId;
    }
  }

  if (!employeeId) return null;

  // Find the employee by id
  return employees.find(emp => emp.id === employeeId) || null;
};

// Component for rendering a group of tool calls
const ToolCallGroup = ({ tools, employees }: { tools: ToolBlock[]; employees?: Employee[] }) => {
  const [expanded, setExpanded] = useState(false);
  const { whitelabelSettings } = useWhitelabel();
  const employeeTerm = whitelabelSettings?.employeeTermSingular || "Employee";

  if (!tools || tools.length === 0) return null;

  const lastTool = tools[tools.length - 1];
  const runningTools = tools.filter((t) => t.status === "running");
  const isLoading = runningTools.length > 0;

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <Box sx={{ my: 2 }}>
      <Card
        variant="outlined"
        sx={{
          bgcolor: "transparent",
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}
          onClick={handleToggle}
        >
          <AvatarGroup
            max={4}
            sx={{ "& .MuiAvatar-root": { width: 32, height: 32 } }}
          >
            {tools.map((tool, idx) => {
              const faviconUrl = getFaviconUrl(tool.name || "", tool.args, tool.logo);
              // Check if this is a delegate_task and we have an employee for it
              const delegateEmployee = getEmployeeForDelegateTask(tool, employees);
              const avatarSrc = delegateEmployee?.picture
                ? addImageDimensions(delegateEmployee.picture, 64, 64)
                : faviconUrl || undefined;
              return (
                <Avatar
                  key={idx}
                  src={avatarSrc}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor:
                      tool.status === "running" || avatarSrc
                        ? undefined
                        : "primary.main",
                  }}
                >
                  {!avatarSrc && getToolIcon(tool.name || "")}
                </Avatar>
              );
            })}
          </AvatarGroup>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight="medium">
              {tools.length > 1
                ? `${tools.length} steps`
                : (() => {
                    // For delegate_task, show employee name (title) if available
                    const delegateEmployee = getEmployeeForDelegateTask(lastTool, employees);
                    if (delegateEmployee) {
                      return `${delegateEmployee.name} (${delegateEmployee.title})`;
                    }
                    return lastTool.displayName || getToolDisplayName(lastTool.name || "", employeeTerm) || "Tool";
                  })()}
            </Typography>
            {isLoading ? (
              <Box>
                <Skeleton
                  variant="text"
                  width="100%"
                  height={16}
                  sx={{ mb: 0.5 }}
                  animation="wave"
                />
                <Skeleton
                  variant="text"
                  width="80%"
                  height={16}
                  animation="wave"
                />
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {(() => {
                  // Get first argument value for preview
                  if (lastTool.args && typeof lastTool.args === "object") {
                    // Unwrap LangChain format: {"args": [...], "config": {}}
                    let displayArgs = lastTool.args;
                    if (lastTool.args.args && Array.isArray(lastTool.args.args) && lastTool.args.args.length > 0) {
                      displayArgs = lastTool.args.args[0];
                    }

                    const firstValue = Object.values(displayArgs)[0];
                    if (firstValue) {
                      // Handle objects and arrays by JSON stringifying them
                      let valueStr: string;
                      if (typeof firstValue === "object") {
                        try {
                          valueStr = JSON.stringify(firstValue);
                        } catch {
                          valueStr = "Completed";
                        }
                      } else {
                        valueStr = String(firstValue);
                      }
                      return valueStr.length > 100
                        ? valueStr.substring(0, 100) + "..."
                        : valueStr;
                    }
                  }
                  return "Completed";
                })()}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={handleToggle}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider />
          <Box sx={{ p: 2 }}>
            {tools.map((tool, idx) => {
              const faviconUrl = getFaviconUrl(tool.name || "", tool.args, tool.logo);
              // Check if this is a delegate_task and we have an employee for it
              const delegateEmployee = getEmployeeForDelegateTask(tool, employees);
              const expandedAvatarSrc = delegateEmployee?.picture
                ? addImageDimensions(delegateEmployee.picture, 48, 48)
                : faviconUrl || undefined;
              // Get display name - use employee name (title) for delegate_task if available
              const toolDisplayName = delegateEmployee
                ? `${delegateEmployee.name} (${delegateEmployee.title})`
                : tool.displayName || getToolDisplayName(tool.name || "", employeeTerm) || "Tool";
              return (
                <Box key={idx} sx={{ mb: idx < tools.length - 1 ? 2 : 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Avatar
                      src={expandedAvatarSrc}
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor:
                          tool.status === "running" || expandedAvatarSrc
                            ? undefined
                            : "primary.main",
                      }}
                    >
                      {!expandedAvatarSrc && getToolIcon(tool.name || "")}
                    </Avatar>
                    <Typography variant="body2" fontWeight="medium">
                      {toolDisplayName}
                    </Typography>
                    {tool.status && <Chip size="small" label={tool.status} />}
                  </Box>

                  {tool.args && (
                    <Box sx={{ ml: 4, mb: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="medium"
                        sx={{ mb: 1, display: "block" }}
                      >
                        Arguments:
                      </Typography>
                      <ToolArguments args={tool.args} />
                    </Box>
                  )}

                  {tool.output && (
                    <Box sx={{ ml: 4 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="medium"
                      >
                        Output:
                      </Typography>
                      {/* Check if output contains install_required for Composio toolkit */}
                      {(() => {
                        try {
                          const parsed = JSON.parse(tool.output);
                          // Composio toolkit install required - show install button with logo
                          if (parsed.status === "install_required" && parsed.slug) {
                            return (
                              <Box sx={{ mt: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                                  {parsed.logo && (
                                    <Avatar
                                      src={parsed.logo}
                                      sx={{ width: 32, height: 32 }}
                                    >
                                      {parsed.toolkit?.[0]?.toUpperCase()}
                                    </Avatar>
                                  )}
                                  <Typography variant="body2">
                                    {parsed.message}
                                  </Typography>
                                </Box>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  startIcon={<Globe size={16} />}
                                  onClick={() => {
                                    // Dispatch custom event to open Composio installer
                                    window.dispatchEvent(new CustomEvent("openComposioInstaller", {
                                      detail: {
                                        slug: parsed.slug,
                                        composioSlug: parsed.composioSlug,
                                        logo: parsed.logo,
                                        name: parsed.toolkit?.charAt(0).toUpperCase() + parsed.toolkit?.slice(1)
                                      }
                                    }));
                                  }}
                                >
                                  Connect {parsed.toolkit?.charAt(0).toUpperCase() + parsed.toolkit?.slice(1)}
                                </Button>
                              </Box>
                            );
                          }
                        } catch {
                          // Not JSON or doesn't have the expected structure
                        }
                        // Default: show raw output
                        return (
                          <Box
                            component="pre"
                            sx={{
                              mt: 0.5,
                              p: 1,
                              bgcolor: "background.default",
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              overflow: "auto",
                              maxHeight: "300px",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {tool.output}
                          </Box>
                        );
                      })()}
                    </Box>
                  )}

                  {idx < tools.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Card>
    </Box>
  );
};

// Main component for rendering structured message blocks
export const StructuredMessageContent = ({
  blocks,
  loading,
  messageId,
  employees,
}: {
  blocks?: MessageBlock[];
  loading?: boolean;
  messageId?: string;
  employees?: Employee[];
}) => {
  if (!blocks || blocks.length === 0) {
    if (loading) {
      return (
        <Box>
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="80%" />
        </Box>
      );
    }
    return null;
  }

  // Group consecutive tool calls together
  const groupedBlocks: MessageBlock[] = [];
  let currentToolGroup: ToolBlock[] = [];

  for (const block of blocks) {
    if (block.type === "tool") {
      currentToolGroup.push(block);
    } else {
      // If we have pending tool calls, add them as a group
      if (currentToolGroup.length > 0) {
        groupedBlocks.push({
          type: "tool-group",
          tools: currentToolGroup,
        } as MessageBlock);
        currentToolGroup = [];
      }
      groupedBlocks.push(block);
    }
  }

  // Add any remaining tool calls
  if (currentToolGroup.length > 0) {
    groupedBlocks.push({
      type: "tool-group",
      tools: currentToolGroup,
    } as MessageBlock);
  }

  return (
    <Box>
      {groupedBlocks.map((block, idx) => {
        if (block.type === "text") {
          // Check if there are any citations blocks following this text
          const hasCitations = groupedBlocks.some(
            (b) => b.type === "citations"
          );

          return (
            <Box key={idx}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Override link rendering to handle citation references
                  a(props) {
                    const { href, children } = props;
                    return (
                      <Link
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        {children}
                      </Link>
                    );
                  },
                  // Override paragraph and text to convert citation markers like [1] to clickable links
                  p(props) {
                    const { children, ...rest } = props;

                    if (!hasCitations) {
                      return <p {...rest}>{children}</p>;
                    }

                    // Convert citation markers [1], [2], etc. to clickable elements
                    const processNode = (node: any): any => {
                      if (typeof node === "string") {
                        // Match citation patterns like [1], [2][3], etc.
                        const parts = node.split(/(\[\d+\])/g);

                        return parts.map((part, i) => {
                          const match = part.match(/^\[(\d+)\]$/);
                          if (match) {
                            const citationNum = match[1];
                            const citationId = messageId
                              ? `citation-${messageId}-${citationNum}`
                              : `citation-${citationNum}`;
                            return (
                              <Box
                                key={i}
                                component="a"
                                href={`#${citationId}`}
                                onClick={(e: any) => {
                                  e.preventDefault();

                                  // First, find the citations card and expand it if needed
                                  const citationsCard =
                                    document.querySelector(".citations-card");
                                  if (citationsCard) {
                                    const expandButton =
                                      citationsCard.querySelector(
                                        '[aria-label="expand"]'
                                      ) as HTMLElement;

                                    // Check if citations are collapsed by seeing if the element exists
                                    const element =
                                      document.getElementById(citationId);

                                    if (!element && expandButton) {
                                      // Citations are collapsed, expand first
                                      expandButton.click();
                                      // Wait for expansion animation, then scroll
                                      setTimeout(() => {
                                        const nowVisibleElement =
                                          document.getElementById(citationId);
                                        if (nowVisibleElement) {
                                          nowVisibleElement.scrollIntoView({
                                            behavior: "smooth",
                                            block: "nearest",
                                          });
                                          nowVisibleElement.style.backgroundColor =
                                            "rgba(25, 118, 210, 0.15)";
                                          setTimeout(() => {
                                            nowVisibleElement.style.backgroundColor =
                                              "";
                                          }, 2000);
                                        }
                                      }, 350);
                                    } else if (element) {
                                      // Citations already expanded, just scroll
                                      element.scrollIntoView({
                                        behavior: "smooth",
                                        block: "nearest",
                                      });
                                      element.style.backgroundColor =
                                        "rgba(25, 118, 210, 0.15)";
                                      setTimeout(() => {
                                        element.style.backgroundColor = "";
                                      }, 2000);
                                    }
                                  }
                                }}
                                sx={{
                                  fontSize: "0.75em",
                                  verticalAlign: "super",
                                  textDecoration: "none !important",
                                  color: "primary.main",
                                  cursor: "pointer",
                                  display: "inline",
                                  "&:hover": {
                                    opacity: 0.8,
                                  },
                                }}
                              >
                                {part}
                              </Box>
                            );
                          }
                          return part;
                        });
                      } else if (
                        React.isValidElement(node) &&
                        (node.props as any).children
                      ) {
                        // Recursively process children
                        return React.cloneElement(node as any, {
                          ...(node.props as any),
                          children: React.Children.map(
                            (node.props as any).children,
                            processNode
                          ),
                        });
                      }
                      return node;
                    };

                    const processedChildren = React.Children.map(
                      children,
                      processNode
                    );

                    return <p {...rest}>{processedChildren}</p>;
                  },
                }}
              >
                {block.content || ""}
              </ReactMarkdown>
            </Box>
          );
        } else if (block.type === "tool-group") {
          // Check if it's a save_memory tool - render specially
          const memoryTools = block.tools.filter((t: ToolBlock) => t.name === "save_memory");
          const otherTools = block.tools.filter((t: ToolBlock) => t.name !== "save_memory");

          return (
            <React.Fragment key={idx}>
              {/* Render memory tools as special cards */}
              {memoryTools.map((tool: ToolBlock, memIdx: number) => (
                <MemoryToolCard key={`mem-${memIdx}`} tool={tool} />
              ))}
              {/* Render other tools normally */}
              {otherTools.length > 0 && <ToolCallGroup tools={otherTools} employees={employees} />}
            </React.Fragment>
          );
        } else if (block.type === "install") {
          // Render install button for Composio toolkit installation
          const installBlock = block as InstallBlock;
          console.log("Rendering install block:", installBlock);
          const installData = {
            slug: installBlock.slug || "",
            composioSlug: installBlock.composioSlug || "",
            logo: installBlock.logo || "",
            name: installBlock.name || "",
            message: installBlock.message || "",
          };
          return <ComposioInstallButton key={idx} installData={installData} />;
        } else if (block.type === "reasoning") {
          return <ReasoningBlock key={idx} content={block.content} />;
        } else if (block.type === "citations") {
          const citations = block.citations;
          const [citationsExpanded, setCitationsExpanded] = useState(false);

          return (
            <Box key={idx} sx={{ my: 2 }} className="citations-card">
              <Card
                variant="outlined"
                sx={{
                  bgcolor: "transparent",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}
                  onClick={() => setCitationsExpanded(!citationsExpanded)}
                >
                  <AvatarGroup
                    max={4}
                    sx={{ "& .MuiAvatar-root": { width: 32, height: 32 } }}
                  >
                    {citations.map((citation: any, citIdx: number) => {
                      const domain = getDomainFromUrl(citation.url);
                      const faviconUrl = domain
                        ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128`
                        : null;

                      return (
                        <Avatar
                          key={citIdx}
                          src={faviconUrl || undefined}
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: faviconUrl ? undefined : "info.main",
                          }}
                        >
                          {!faviconUrl && <FileText size={16} />}
                        </Avatar>
                      );
                    })}
                  </AvatarGroup>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {citations.length === 1
                        ? "1 source"
                        : `${citations.length} sources`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {citations[0]?.title && citations[0].title.length > 60
                        ? citations[0].title.substring(0, 60) + "..."
                        : citations[0]?.title || "View sources"}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCitationsExpanded(!citationsExpanded);
                    }}
                    aria-label="expand"
                  >
                    {citationsExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={citationsExpanded} timeout="auto" unmountOnExit>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    {citations.map((citation: any, citIdx: number) => {
                      const domain = getDomainFromUrl(citation.url);
                      const faviconUrl = domain
                        ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128`
                        : null;

                      const citationId = messageId
                        ? `citation-${messageId}-${citIdx + 1}`
                        : `citation-${citIdx + 1}`;

                      return (
                        <Box
                          key={citIdx}
                          id={citationId}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: citIdx < citations.length - 1 ? 1.5 : 0,
                            p: 1,
                            borderRadius: 1,
                            transition: "background-color 0.3s",
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              minWidth: 20,
                              fontWeight: "bold",
                              color: "primary.main",
                            }}
                          >
                            [{citIdx + 1}]
                          </Typography>
                          <Avatar
                            src={faviconUrl || undefined}
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: faviconUrl ? undefined : "info.main",
                            }}
                          >
                            {!faviconUrl && <FileText size={16} />}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Link
                              href={citation.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{
                                fontSize: "0.875rem",
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {citation.title || citation.url}
                            </Link>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {domain}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              </Card>
            </Box>
          );
        } else if (block.type === "file") {
          const fileBlock = block as FileBlock;
          const formatFileSize = (bytes: number): string => {
            if (bytes < 1024) return `${bytes} B`;
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
          };

          const getFileIcon = (mimeType: string) => {
            if (mimeType.includes("pdf")) return "📄";
            if (mimeType.includes("word") || mimeType.includes("docx")) return "📝";
            if (mimeType.includes("csv") || mimeType.includes("spreadsheet")) return "📊";
            return "📎";
          };

          const handleDownload = () => {
            try {
              // Decode base64 content
              const binaryString = atob(fileBlock.content);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              const blob = new Blob([bytes], { type: fileBlock.mimeType });
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = sanitizeFilename(fileBlock.filename, "download");
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            } catch (error) {
              console.error("Error downloading file:", error);
            }
          };

          return (
            <Box key={idx} sx={{ my: 2 }}>
              <Card
                variant="outlined"
                sx={{
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      width: 48,
                      height: 48,
                    }}
                  >
                    <FileText size={24} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getFileIcon(fileBlock.mimeType)} {fileBlock.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fileBlock.filename} • {formatFileSize(fileBlock.size)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Download size={16} />}
                    onClick={handleDownload}
                    sx={{ flexShrink: 0 }}
                  >
                    Download
                  </Button>
                </Box>
              </Card>
            </Box>
          );
        } else if (block.type === "attachments") {
          const attachmentBlock = block as AttachmentBlock;
          const attachments = attachmentBlock.attachments || [];

          if (attachments.length === 0) return null;

          // Separate images and documents
          const images = attachments.filter((att) => att.type.startsWith("image/"));
          const documents = attachments.filter((att) => !att.type.startsWith("image/"));

          return (
            <Box key={idx} sx={{ mt: 2, mb: 1 }}>
              {/* Render images as thumbnails with tooltip preview */}
              {images.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: documents.length > 0 ? 1 : 0 }}>
                  {images.map((att, attIdx) => (
                    <Tooltip
                      key={attIdx}
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Box
                            component="img"
                            src={att.content}
                            alt={att.name}
                            sx={{
                              maxWidth: 400,
                              maxHeight: 300,
                              objectFit: "contain",
                            }}
                          />
                          <Typography variant="caption" sx={{ display: "block", mt: 0.5, textAlign: "center" }}>
                            {att.name}
                          </Typography>
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <Box
                        component="img"
                        src={att.content}
                        alt={att.name}
                        sx={{
                          maxWidth: 200,
                          maxHeight: 150,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          objectFit: "cover",
                          cursor: "pointer",
                          "&:hover": {
                            opacity: 0.9,
                          },
                        }}
                        onClick={() => {
                          // Open image in new tab
                          const win = window.open();
                          if (win) {
                            win.document.write(`<img src="${att.content}" alt="${att.name}" style="max-width: 100%; height: auto;" />`);
                            win.document.title = att.name;
                          }
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              )}

              {/* Render documents as chips with tooltip preview */}
              {documents.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {documents.map((att, attIdx) => (
                    <Tooltip
                      key={attIdx}
                      title={
                        <Box sx={{ maxWidth: 400, maxHeight: 300, overflow: "auto", p: 1 }}>
                          <Typography variant="caption" sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                            {att.content && att.content.length > 1000
                              ? att.content.substring(0, 1000) + "..."
                              : att.content || "No preview available"}
                          </Typography>
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <Chip
                        icon={<Paperclip size={16} />}
                        label={att.name}
                        size="small"
                        variant="outlined"
                        sx={{ maxWidth: 200, cursor: "pointer" }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              )}
            </Box>
          );
        } else if (block.type === "memory") {
          const memoryBlock = block as MemoryBlock;

          // Get category label and color
          const getCategoryLabel = (category?: string) => {
            switch (category) {
              case "preference":
                return "Preference";
              case "fact":
                return "Fact";
              case "instruction":
                return "Instruction";
              case "context":
                return "Context";
              default:
                return "Memory";
            }
          };

          return (
            <Box key={idx} sx={{ my: 2 }}>
              <Card
                variant="outlined"
                sx={{
                  bgcolor: "transparent",
                  borderColor: "primary.main",
                  borderWidth: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: memoryBlock.status === "saved" ? "primary.main" : "error.main",
                    }}
                  >
                    {memoryBlock.status === "saved" ? (
                      <CheckCircle size={20} />
                    ) : (
                      <Sparkles size={20} />
                    )}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Memory Saved
                      </Typography>
                      {memoryBlock.category && (
                        <Chip
                          label={getCategoryLabel(memoryBlock.category)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {memoryBlock.content}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Settings />}
                    onClick={() => {
                      // Dispatch event to open memories dialog
                      window.dispatchEvent(new CustomEvent("openMemoriesDialog"));
                    }}
                  >
                    Manage
                  </Button>
                </Box>
              </Card>
            </Box>
          );
        }
        return null;
      })}
      {loading && (
        <Box>
          <LoadingCursor loading={true}>{""}</LoadingCursor>
        </Box>
      )}
    </Box>
  );
};
